import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Placeholder } from '@/src/elements/placeholder'
import { Typography } from '@/src/elements/text/Typography'
import type {
  TGetNotificationSettingsData,
  TGetNotificationSettingsPayload,
} from '@/src/features/notification/api/types/getNotificationSettings'
import { useNotificationSettingsQuery } from '@/src/features/notification/services/useNotificationSettingsQuery'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { ICON } from '@/src/general/enums/icons'
import { useRoutes } from '@/src/general/utils/routes'

import { SettingsCardCommunities } from '../../components/SettingsCardCommunities'

const getCommunityNotificationSettings = (notificationsSettings: TGetNotificationSettingsData) =>
  Object.keys(notificationsSettings.publications).map((communityId) => ({
    communityId,
    communityName: notificationsSettings.publications[communityId].communityName,
    publications: notificationsSettings.publications[communityId],
    announcements: notificationsSettings.announcements[communityId],
  }))

export const NotificationsCommunitiesList = ({
  isSubscriptionAlive,
  updateParametersToken,
}: TGetNotificationSettingsPayload): ReactNode => {
  const router = useRouter()
  const { routes } = useRoutes()

  const { data, error } = useNotificationSettingsQuery(
    isSubscriptionAlive
      ? {
          isSubscriptionAlive,
          updateParametersToken,
        }
      : {
          updateParametersToken,
        },
    !!updateParametersToken,
  )

  if (error) return <ErrorMessage error={error} defaultErrorKey="common.error.unknown" />

  if (!data) {
    return (
      <>
        {Array.from({ length: 3 }, (_, index) => (
          <Placeholder key={index} className="h-40" />
        ))}
      </>
    )
  }

  const communityNotificationSettings = getCommunityNotificationSettings(data)

  if (communityNotificationSettings.length === 0) {
    return (
      <View className="flex-1 items-center justify-center gap-4 p-4">
        <Typography colors="primary">
          <FormattedMessage id="community.communities.none-joined" />
        </Typography>
        <Button
          onPress={() => router.navigate(routes.communities())}
          colors="primary"
          variant="solid"
          endIcon={ICON.SEARCH}
        >
          <FormattedMessage id="app.discover" />
        </Button>
      </View>
    )
  }

  return (
    <>
      {communityNotificationSettings.map(({ communityId, communityName, publications, announcements }) => (
        <SettingsCardCommunities
          key={communityId}
          updateParametersToken={updateParametersToken}
          communityId={communityId}
          communityName={communityName}
          publications={publications}
          announcements={announcements}
        />
      ))}
    </>
  )
}
